// Drifting billboard clouds.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { canvasTex } from '../lib/factory.js';

export let clouds, cloudMat, cloudShade;

export function buildClouds() {
  // clouds drifting around the diorama
  // soft billboard clouds: clusters of blurred puffs, lit from above
  clouds = new THREE.Group();
  scene.add(clouds);
  const puffTex = canvasTex(128, 128, (c) => {
    for (let k = 0; k < 14; k++) {
      const x = 64 + rr(-26, 26),
        y = 64 + rr(-18, 18),
        r = rr(22, 40),
        g = c.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(255,255,255,.55)');
      g.addColorStop(0.6, 'rgba(250,252,255,.25)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      c.fillStyle = g;
      c.fillRect(0, 0, 128, 128);
    }
  });
  // all puffs in one instanced draw: a billboard shader turns each quad to face the camera (they were ~190 sprites, one draw call each)
  const cloudU = {
    map: { value: puffTex },
    uLit: { value: new THREE.Color(0xffffff) },
    uShade: { value: new THREE.Color(0xc9d3df) },
  };
  cloudMat = { color: cloudU.uLit.value };
  cloudShade = { color: cloudU.uShade.value }; // setMode() recolours these for night
  {
    const puffs = [];
    for (let k = 0; k < 16; k++) {
      const a = rr(-PI, PI),
        r = rr(150, 330),
        cx = Math.cos(a) * r,
        cy = rr(70, 120),
        cz = Math.sin(a) * r;
      const n = 8 + Math.floor(rnd() * 8),
        span = rr(18, 40);
      // shaded undersides first so the lit tops draw over them
      for (let i = 0; i < n; i++) {
        const sh = i < n / 3,
          sc = rr(14, 28);
        puffs.push({
          x: cx + rr(-span, span),
          y: cy + (sh ? -3 : rr(0, 6)),
          z: cz + rr(-span / 3, span / 3),
          sx: sc * 1.6,
          sy: sc,
          sh: sh ? 1 : 0,
        });
      }
    }
    puffs.sort((a, b) => b.sh - a.sh);
    const geo = new THREE.PlaneGeometry(1, 1);
    geo.setAttribute(
      'aShade',
      new THREE.InstancedBufferAttribute(new Float32Array(puffs.map((p) => p.sh)), 1),
    );
    const mat = new THREE.ShaderMaterial({
      uniforms: cloudU,
      transparent: true,
      depthWrite: false,
      vertexShader: `attribute float aShade; varying vec2 vUv; varying float vShade;
      void main() { vUv = uv; vShade = aShade; vec4 c = modelViewMatrix * instanceMatrix * vec4(0., 0., 0., 1.);
        c.xy += position.xy * vec2(length(instanceMatrix[0].xyz), length(instanceMatrix[1].xyz)); gl_Position = projectionMatrix * c; }`,
      fragmentShader: `uniform sampler2D map; uniform vec3 uLit, uShade; varying vec2 vUv; varying float vShade;
      void main() { vec4 t = texture2D(map, vUv); gl_FragColor = vec4(mix(uLit, uShade, vShade) * t.rgb, t.a);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    });
    const im = new THREE.InstancedMesh(geo, mat, puffs.length),
      o = new THREE.Object3D();
    puffs.forEach((p, i) => {
      o.position.set(p.x, p.y, p.z);
      o.scale.set(p.sx, p.sy, 1);
      o.updateMatrix();
      im.setMatrixAt(i, o.matrix);
    });
    im.frustumCulled = false;
    im.castShadow = im.receiveShadow = false;
    clouds.add(im);
  }
}
